
import { useState, useEffect, useRef, useMemo } from "react"
import { Button } from "@components/react/pricing/ui/button"
import { Check, Info, ChevronDown, Plus, ArrowRight } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

interface SuccessPlanProps {
  openQuoteModalWithPlan: (planName: string) => void
}

export function OptimizedSuccessPlans({ openQuoteModalWithPlan }: SuccessPlanProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [hoveredInfo, setHoveredInfo] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [activeMobilePlan, setActiveMobilePlan] = useState("standard")
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isButtonHovered, setIsButtonHovered] = useState(false)
  const compareButtonRef = useRef<HTMLButtonElement>(null)
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  // Plan definitions with clear structure - memoized to prevent unnecessary re-renders
  const plans = useMemo(
    () => [
      {
        id: "standard",
        name: "Standard",
        price: "Included",
        priceSubtext: "in SaaS subscriptions",
        description: "Basic support plan for small organizations",
        color: "#f5f5f7",
        hoverColor: "#ebebf0",
        textColor: "#1d1d1f",
        bgColor: "#f5f5f7", // Light gray - basic tier
        headerBgColor: "#f5f5f7",
        checkColor: "#3037e2", // AvePoint Indigo
        checkBgColor: "#f1f8ff",
      },
      {
        id: "advanced",
        name: "Advanced",
        price: "$6,172",
        priceSubtext: "per organization / month",
        description: "Enhanced support for growing organizations",
        color: "#f5f5f7",
        hoverColor: "#ebebf0",
        textColor: "#1d1d1f",
        bgColor: "#f0f4ff", // Light blue - professional tier
        headerBgColor: "#f0f4ff",
        checkColor: "#3037e2", // AvePoint Indigo
        checkBgColor: "#f1f8ff",
      },
      {
        id: "enterprise",
        name: "Enterprise",
        price: "$9,052",
        priceSubtext: "per organization / month",
        description: "Comprehensive support for large organizations",
        color: "#f5f5f7",
        hoverColor: "#ebebf0",
        textColor: "#1d1d1f",
        bgColor: "#f3f0ff", // Light purple - advanced tier
        headerBgColor: "#f3f0ff",
        checkColor: "#5e35b1", // Purple
        checkBgColor: "#f5f0ff",
      },
      {
        id: "elite",
        name: "Elite",
        price: "$11,520",
        priceSubtext: "per organization / month",
        description: "Premium support for enterprise organizations",
        color: "#f5f5f7",
        hoverColor: "#ebebf0",
        textColor: "#1d1d1f",
        bgColor: "#fff8e1", // Light gold - premium tier
        headerBgColor: "#fff8e1",
        checkColor: "#ef483d", // AvePoint Red
        checkBgColor: "#fff5f5",
      },
    ],
    [],
  )

  // Feature categories - memoized to prevent unnecessary re-renders
  const featureCategories = useMemo(
    () => [
      {
        id: "support",
        title: "Support & Availability",
        description: "Technical support and product updates",
        features: [
          {
            name: "24/7/365 global technical support",
            info: "Phone, email, and chat support available around the clock",
            values: ["Yes", "Yes", "Yes", "Yes"],
          },
          {
            name: "Product updates and fixes",
            info: "Regular software updates and bug fixes",
            values: ["Yes", "Yes", "Yes", "Yes"],
          },
          {
            name: "Semi-annual Technical Roadmap Sessions",
            info: "Bi-annual sessions to discuss upcoming product features",
            values: ["Yes", "Yes", "Yes", "Yes"],
          },
        ],
      },
      {
        id: "account",
        title: "Account Management",
        description: "Customer success and business reviews",
        features: [
          {
            name: "Customer Success Manager",
            info: "Dedicated resource focused on your success and adoption",
            values: ["Pooled", "Assigned", "Assigned", "Assigned"],
          },
          {
            name: "Executive business reviews and project alignment",
            info: "Regular meetings to ensure alignment with business goals",
            values: ["—", "Basic", "Detailed", "Detailed"],
          },
          {
            name: "Solution value workshops and solution advisory",
            info: "Workshops to maximize the value of your investment",
            values: ["—", "—", "Yes", "Yes"],
          },
        ],
      },
      {
        id: "technical",
        title: "Technical Services",
        description: "Technical expertise and architecture support",
        features: [
          {
            name: "Technical Account Manager",
            info: "Technical resource providing architecture review and best practices",
            values: ["—", "50%", "75%", "100%"],
          },
          {
            name: "Primary Support Engineer",
            info: "Named support engineer for case routing and escalation priority",
            values: ["—", "—", "Yes", "Yes"],
          },
          {
            name: "Specialized architecture reviews",
            info: "In-depth analysis of your technical architecture",
            values: ["—", "—", "Yes", "Yes"],
          },
        ],
      },
      {
        id: "implementation",
        title: "Implementation & Configuration",
        description: "Setup, configuration, and best practices",
        features: [
          {
            name: "Setup and configuration best practices",
            info: "Guidance on optimal configuration for your environment",
            values: ["Self-service", "Fundamental", "Expert-led", "Expert-led"],
          },
          {
            name: "Health checks and recommendations",
            info: "Regular system evaluations to ensure optimal performance",
            values: ["—", "Yes", "Yes", "Yes"],
          },
          {
            name: "Implementation planning assistance",
            info: "Help with planning your implementation strategy",
            values: ["—", "Basic", "Comprehensive", "Comprehensive"],
          },
        ],
      },
      {
        id: "advanced",
        title: "Advanced Services",
        description: "Proactive monitoring and project planning",
        features: [
          {
            name: "Project planning and delivery prioritization",
            info: "Strategic planning for implementation and deployment",
            values: ["—", "—", "Yes", "Yes"],
          },
          {
            name: "Proactive Support & Monitoring",
            info: "Real-time monitoring and issue prevention",
            values: ["—", "—", "—", "Yes"],
          },
          {
            name: "Custom development assistance",
            info: "Help with custom integrations and development",
            values: ["—", "—", "—", "Yes"],
          },
        ],
      },
    ],
    [],
  )

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!sectionRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(sectionRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  // Check if we're on mobile - with debounce to prevent excessive re-renders
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const checkIfMobile = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768)
      }, 100)
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile)
      clearTimeout(timeoutId)
    }
  }, [])

  // Auto-expand the first category after a short delay to show interactivity
  // But only on desktop, not on mobile, and only when visible
  useEffect(() => {
    if (!hasInteracted && !isMobile && isVisible) {
      const timer = setTimeout(() => {
        setActiveCategory("support")
        setHasInteracted(true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [hasInteracted, isMobile, isVisible])

  // Add a subtle pulse animation to the compare button when the page loads on mobile
  // Only run this effect when the section is visible
  useEffect(() => {
    if (isMobile && compareButtonRef.current && !hasInteracted && isVisible) {
      // Use GSAP for a subtle pulse animation if available
      if (typeof window !== "undefined" && window.gsap) {
        // Simplified animation for mobile to improve performance
        const timeline = window.gsap.timeline({ repeat: 1 })
        timeline.to(compareButtonRef.current, {
          scale: 1.03,
          duration: 0.4,
          ease: "power1.inOut",
        })
        timeline.to(compareButtonRef.current, {
          scale: 1,
          duration: 0.4,
          ease: "power1.inOut",
        })
      }
    }
  }, [isMobile, hasInteracted, isVisible])

  const toggleCategory = (categoryId: string) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId)
    setHasInteracted(true)
  }

  // Get plan index by id - memoized for performance
  const getPlanIndexById = useMemo(
    () => (id: string) => {
      return plans.findIndex((plan) => plan.id === id)
    },
    [plans],
  )

  // Simplified mobile animations
  const mobileAnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  }

  // Only render content when visible (lazy loading)
  if (!isVisible) {
    return <div ref={sectionRef} className="min-h-[200px]" style={{ contentVisibility: "auto" }}></div>
  }

  const bottomCta = (
    <div className="bg-[#ffffff] rounded-2xl p-6 md:p-10 text-center mt-8 md:mt-12">
        <h3 className="text-2xl md:text-3xl font-bold text-[#1d1d1f] mb-3 md:mb-4">
          Need Help Choosing the Right Plan?
        </h3>
        <p className="text-sm md:text-base text-[#424245] mb-6 md:mb-8 max-w-2xl mx-auto">
          Our team of experts can help you select the perfect plan based on your specific needs.
        </p>
        <Button
          className="bg-[#3037e2] hover:bg-[#2731c8] text-white transition-all duration-300 px-6 md:px-8 text-sm md:text-base group"
          onClick={() => openQuoteModalWithPlan("Custom Success Plan")}
        >
          <span>Get Custom Quote</span>
          <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Button>
      </div>
  );
  if (!isMobile) {
    return (
      <div ref={sectionRef} className="w-full success-plans-container" style={{ contentVisibility: "auto" }}>
        {/* Desktop instruction text */}
        <div className="text-center mb-6 text-sm text-[#86868b]">
          Click on a category to expand and view detailed features
        </div>

        {/* Apple-style pricing table - Desktop Version */}
        <div className="bg-white rounded-2xl border border-[#e5e5e7] overflow-hidden shadow-sm mb-12">
          {/* Plan headers */}
          <div className="grid grid-cols-5 border-b border-[#e5e5e7]">
            <div className="p-4 border-r border-[#e5e5e7]"></div>
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`p-6 text-center border-r border-[#e5e5e7] last:border-r-0 flex flex-col items-center relative overflow-hidden transition-all duration-300`}
                style={{
                  backgroundColor: hoveredPlan === plan.id ? plan.hoverColor : plan.bgColor,
                }}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                <h3 className="text-lg font-semibold text-[#1d1d1f] mb-2">{plan.name}</h3>
                <div className="text-2xl font-bold text-[#1d1d1f] mb-1">{plan.price}</div>
                <div className="text-sm text-[#86868b] mb-4">{plan.priceSubtext}</div>
                <Button
                  className={`bg-[#3037e2] hover:bg-[#2731c8] text-white text-sm px-6 group transition-all duration-300`}
                  onClick={() => openQuoteModalWithPlan(`${plan.name} Success Plan`)}
                >
                  <span>Get Custom Quote</span>
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>
            ))}
          </div>

          {/* Feature categories and their features */}
          {featureCategories.map((category) => (
            <div key={category.id} className="border-b border-[#e5e5e7] last:border-b-0">
              {/* Category header */}
              <div
                className="grid grid-cols-5 bg-[#f5f5f7] cursor-pointer hover:bg-[#ebebf0] transition-colors duration-300"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="p-5 font-medium text-[#1d1d1f] border-r border-[#e5e5e7] flex items-center justify-between">
                  <div>
                    <div>{category.title}</div>
                    <div className="text-xs text-[#86868b] mt-1">{category.description}</div>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-[#3037e2] transition-transform duration-300 ${
                      activeCategory === category.id ? "transform rotate-180" : ""
                    }`}
                  />
                </div>
                {plans.map((plan, index) => (
                  <div key={index} className="p-4 border-r border-[#e5e5e7] last:border-r-0"></div>
                ))}
              </div>

              {/* Features for this category */}
              <AnimatePresence>
                {activeCategory === category.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {category.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className={`grid grid-cols-5 ${
                          featureIndex !== category.features.length - 1 ? "border-b border-[#e5e5e7]" : ""
                        } hover:bg-[#f9f9fb] transition-colors duration-200`}
                      >
                        <div className="p-5 border-r border-[#e5e5e7] flex items-center">
                          <div className="flex items-center">
                            <span className="text-[#424245]">{feature.name}</span>
                            {feature.info && (
                              <div
                                className="ml-2 relative"
                                onMouseEnter={() => setHoveredInfo(`${category.id}-${featureIndex}`)}
                                onMouseLeave={() => setHoveredInfo(null)}
                              >
                                <Info className="h-4 w-4 text-[#86868b] hover:text-[#3037e2] transition-colors duration-200" />
                                <AnimatePresence>
                                  {hoveredInfo === `${category.id}-${featureIndex}` && (
                                    <motion.div
                                      initial={{ opacity: 0, y: 5 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 5 }}
                                      transition={{ duration: 0.2 }}
                                      className="absolute z-10 left-6 top-0 w-64 p-4 bg-white rounded-lg shadow-lg border border-[#e5e5e7] text-sm text-[#424245]"
                                    >
                                      {feature.info}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            )}
                          </div>
                        </div>
                        {feature.values.map((value, valueIndex) => (
                          <div
                            key={valueIndex}
                            className="p-5 text-center border-r border-[#e5e5e7] last:border-r-0"
                            style={{
                              backgroundColor:
                                valueIndex < plans.length ? `${plans[valueIndex].bgColor}40` : "transparent",
                            }}
                          >
                            {value === "Yes" ? (
                              <div className="flex justify-center">
                                <div
                                  className="h-6 w-6 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: plans[valueIndex].checkBgColor }}
                                >
                                  <Check className="h-4 w-4" style={{ color: plans[valueIndex].checkColor }} />
                                </div>
                              </div>
                            ) : value === "—" ? (
                              <div className="flex justify-center">
                                <div className="h-0.5 w-3 bg-[#d1d1d6] rounded-full"></div>
                              </div>
                            ) : (
                              <span className="text-[#424245] font-medium">{value}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        {bottomCta}
      </div>
      )
  }
  return (
      <div ref={sectionRef} className="w-full success-plans-container" style={{ contentVisibility: "auto" }}>

        {/* Mobile Version - Optimized for performance */}
        <div>
          {/* Simplified segmented control for mobile */}
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-[#f5f5f7] rounded-xl"></div>
            <div className="relative grid grid-cols-4 bg-transparent rounded-xl p-1">
              {plans.map((plan, index) => (
                <button
                  key={index}
                  className="relative py-2.5 text-sm font-medium transition-colors duration-200"
                  onClick={() => setActiveMobilePlan(plan.id)}
                >
                  {activeMobilePlan === plan.id && (
                    <motion.div
                      layoutId="segmentedControlActive"
                      className="absolute inset-0 bg-white rounded-lg shadow-sm"
                      transition={{ type: "spring", bounce: 0.15, duration: 0.4 }}
                      style={{ zIndex: 0 }}
                    />
                  )}
                  <span className={`relative z-10 ${activeMobilePlan === plan.id ? "text-[#1d1d1f]" : "text-[#86868b]"}`}>
                    {plan.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Plan Card - Optimized animations for mobile */}
          {plans.map(
            (plan) =>
              plan.id === activeMobilePlan && (
                <motion.div
                  key={plan.id}
                  {...mobileAnimationProps}
                  className="rounded-2xl border border-[#e5e5e7] overflow-hidden shadow-sm mb-6"
                  style={{
                    backgroundColor: plan.bgColor || "#ffffff",
                  }}
                >
                  <div className="p-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-[#1d1d1f] mb-2">{plan.name}</h3>
                      <div className="text-3xl font-bold text-[#1d1d1f] mb-1">{plan.price}</div>
                      <div className="text-sm text-[#86868b] mb-4">{plan.priceSubtext}</div>
                      <p className="text-sm text-[#424245] mb-6 max-w-md mx-auto">{plan.description}</p>
                      <Button
                        className="bg-[#3037e2] hover:bg-[#2731c8] text-white w-full group"
                        onClick={() => openQuoteModalWithPlan(`${plan.name} Success Plan`)}
                      >
                        <span>Get Custom Quote</span>
                        <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Button>
                    </div>

                    <div className="h-px bg-[#e5e5e7] my-6"></div>

                    <div className="space-y-1">
                      <h4 className="text-sm font-medium text-[#1d1d1f] mb-3">Key Features:</h4>
                      {featureCategories.map((category) =>
                        category.features.map((feature, featureIndex) => {
                          const planIndex = getPlanIndexById(activeMobilePlan)
                          // Only show features that are included or have specific values (not just dashes)
                          if (feature.values[planIndex] !== "—") {
                            return (
                              <div key={`${category.id}-${featureIndex}`} className="flex items-center py-2">
                                {feature.values[planIndex] === "Yes" ? (
                                  <div
                                    className="h-5 w-5 rounded-full flex items-center justify-center mr-3"
                                    style={{
                                      backgroundColor: plans[planIndex].checkBgColor,
                                    }}
                                  >
                                    <Check className="h-3 w-3" style={{ color: plans[planIndex].checkColor }} />
                                  </div>
                                ) : (
                                  <div className="h-5 w-5 flex items-center justify-center mr-3">
                                    <div className="h-1.5 w-1.5 rounded-full bg-[#86868b]"></div>
                                  </div>
                                )}
                                <div className="flex-1">
                                  <span className="text-sm text-[#424245]">{feature.name}</span>
                                  {feature.values[planIndex] !== "Yes" && feature.values[planIndex] !== "—" && (
                                    <span className="text-xs font-medium text-[#3037e2] ml-2">
                                      {feature.values[planIndex]}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          }
                          return null
                        }),
                      )}
                    </div>
                  </div>
                </motion.div>
              ),
          )}

          {/* Compare All Features Button - Simplified for mobile performance */}
          <button
            ref={compareButtonRef}
            onClick={() => {
              setActiveCategory(activeCategory ? null : "support")
              setHasInteracted(true)
            }}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
            className={`w-full bg-white border border-[#e5e5e7] rounded-xl py-4 px-5 text-center mb-8 flex items-center justify-center text-[#3037e2] font-medium hover:bg-[#f5f5f7] transition-all duration-300 shadow-sm ${
              isButtonHovered ? "shadow-md" : ""
            }`}
          >
            <div className="flex items-center">
              {activeCategory ? (
                <>
                  <span>Hide All Features</span>
                  <ChevronDown className="ml-2 h-5 w-5 transform rotate-180 transition-transform duration-300" />
                </>
              ) : (
                <>
                  <span>Compare All Features</span>
                  <Plus className="ml-2 h-5 w-5" />
                </>
              )}
            </div>
          </button>

          {/* Mobile instruction text - Only shown when expanded */}
          {activeCategory !== null && (
            <div className="text-center mb-4 text-xs text-[#86868b]">
              Tap on a category to expand and view detailed features
            </div>
          )}

          {/* Feature Categories - Only shown when expanded - Optimized for mobile */}
          <AnimatePresence>
            {activeCategory !== null && (
              <motion.div {...mobileAnimationProps} className="space-y-4 mb-8">
                {featureCategories.map((category) => (
                  <div
                    key={category.id}
                    className="bg-white rounded-xl border border-[#e5e5e7] shadow-sm overflow-hidden"
                  >
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full flex items-center justify-between p-4 text-left bg-[#f5f5f7] hover:bg-[#ebebf0] transition-colors duration-200"
                    >
                      <div>
                        <h3 className="font-medium text-[#1d1d1f]">{category.title}</h3>
                        <p className="text-xs text-[#86868b] mt-1">{category.description}</p>
                      </div>
                      <ChevronDown
                        className={`h-5 w-5 text-[#3037e2] transition-transform duration-300 ${
                          activeCategory === category.id ? "transform rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Features - Simplified animations for mobile */}
                    <AnimatePresence>
                      {activeCategory === category.id && (
                        <motion.div {...mobileAnimationProps} className="overflow-hidden">
                          <div className="p-4">
                            <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                              {plans.map((plan, i) => (
                                <div
                                  key={i}
                                  className={`text-xs font-medium py-1 px-2 rounded ${
                                    activeMobilePlan === plan.id ? `text-[#3037e2]` : "text-[#86868b]"
                                  }`}
                                  style={{
                                    backgroundColor: activeMobilePlan === plan.id ? plan.bgColor : "transparent",
                                  }}
                                >
                                  {plan.name}
                                </div>
                              ))}
                            </div>

                            <div className="space-y-4">
                              {category.features.map((feature, featureIndex) => (
                                <div key={featureIndex} className="border-t border-[#e5e5e7] pt-3">
                                  <div className="flex items-start mb-2">
                                    <span className="text-sm font-medium text-[#424245]">{feature.name}</span>
                                    {feature.info && (
                                      <button
                                        className="ml-1.5 text-[#86868b] hover:text-[#3037e2] transition-colors duration-200"
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          setHoveredInfo(
                                            hoveredInfo === `${category.id}-${featureIndex}`
                                              ? null
                                              : `${category.id}-${featureIndex}`,
                                          )
                                        }}
                                      >
                                        <Info className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>

                                  {hoveredInfo === `${category.id}-${featureIndex}` && (
                                    <motion.div
                                      {...mobileAnimationProps}
                                      className="mb-3 text-xs text-[#424245] bg-[#f5f5f7] p-3 rounded-md"
                                    >
                                      {feature.info}
                                    </motion.div>
                                  )}

                                  <div className="grid grid-cols-4 gap-2 mt-1">
                                    {feature.values.map((value, valueIndex) => (
                                      <div key={valueIndex} className="flex justify-center">
                                        {value === "Yes" ? (
                                          <div className="h-5 w-5 rounded-full bg-[#f1f8ff] flex items-center justify-center">
                                            <Check className="h-3 w-3 text-[#3037e2]" />
                                          </div>
                                        ) : value === "—" ? (
                                          <div className="h-5 w-5 flex items-center justify-center">
                                            <div className="h-0.5 w-2 bg-[#d1d1d6] rounded-full"></div>
                                          </div>
                                        ) : (
                                          <span className="text-xs font-medium text-[#424245]">{value}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom CTA */}
        {bottomCta}
    </div>
  )
}
